
import { useState, useEffect, useCallback, useRef } from "react";
import { getGuestTrackings } from "../../services/guestTracking.service";
import { useParams } from "react-router-dom";
import DetailDrawer from "./DetailDrawer";
import { LogoLoading } from "../../components/common/utlity/utility";
import { fmtDate, refIcon} from "../../helpers/util";
import { EventBadge } from "../../helpers/csshelper";
import { useTranslation } from "react-i18next";



 const platformIcon = (p) => {
  const icons = { mobile: "📱", desktop: "💻", tablet: "🖥️" };
  return icons[p] || "💻";
};

export default function GuestTrackings() {
  const {t} = useTranslation()
  const { bussinessUuid } = useParams();
  const [dark, setDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setDark(document.documentElement.classList.contains("dark")),
    );
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => obs.disconnect();
  }, []);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [selected, setSelected] = useState(null);
  const [sortCol, setSortCol] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  const searchTimer = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 400);
  };
  const fetchData = useCallback(async () => {
    if (!bussinessUuid) return;
    setLoading(true);
    setError(null);

    try {
      const result = await getGuestTrackings(bussinessUuid, {
        page,
        limit: perPage,
        ...(debouncedSearch && { search: debouncedSearch }),
        sortBy: sortCol,
        sortOrder: sortDir,
      });

      setData(result?.data || []);
      setTotal(result?.total || 0);
      setTotalPages(result?.totalPages || 1);
    } catch (err) {
      setError("Failed to load guest trackings. Please try again.");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [bussinessUuid, page, perPage, debouncedSearch, sortCol, sortDir]);

  useEffect(() => {
    fetchData();
  }, [fetchData, bussinessUuid]);

  // ── Sort handler
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(col);
      setSortDir("asc");
      setPage(1);
    }
  };

  const pageViewCount = data.filter(
    (r) => r.events?.[0]?.type === "PAGE_VIEW",
  ).length;
  const mobileCount = data.filter(
    (r) => r.device?.platform === "mobile",
  ).length;

  const Th = ({ col, children }) => (
    <th
      onClick={() => toggleSort(col)}
      className='px-4 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400
                 uppercase tracking-wider cursor-pointer select-none whitespace-nowrap
                 hover:text-gray-900 dark:hover:text-gray-200 transition-colors'
    >
      {children}
      {sortCol === col ? (
        <span className='ml-1 text-blue-500'>
          {sortDir === "asc" ? "↑" : "↓"}
        </span>
      ) : (
        <span className='ml-1 text-gray-300 dark:text-gray-600'>↕</span>
      )}
    </th>
  );

  const inputBase = `text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 outline-none
    focus:border-blue-400 focus:ring-2 focus:ring-blue-50 dark:focus:ring-blue-900/30
    bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200
    placeholder-gray-400 dark:placeholder-gray-500 transition-all`;

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-[#0f172a] transition-colors duration-300'>
      {/* ─ Header ─ */}
      <div
        className='bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700
                      px-6 py-4 flex items-center justify-between sticky top-0 z-20 shadow-sm dark:shadow-none'
      >
        <div>
          <h1 className='text-xl font-bold text-gray-900 dark:text-gray-100'>
            {t('TRACKING.GUEST_TRACKINGS')}
          </h1>
          <p className='text-sm text-gray-500 dark:text-gray-400 mt-0.5'>
            dukan_buddy&nbsp;·&nbsp;
            <span className='font-semibold text-gray-700 dark:text-gray-300'>
              {total}
            </span>{" "}
           
            {t('TRACKING.TOTAL_RECORDS')}
          </p>
        </div>
        <div className='flex items-center gap-2'>
          {/* Refresh button */}
          <button
            onClick={fetchData}
            disabled={loading}
            className='p-2 rounded-lg border border-gray-200 dark:border-gray-600
                       bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400
                       hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50'
            title={t('EMPLOYEEDASHBOARD.REFRESH')}
          >
            <svg
              className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              viewBox='0 0 24 24'
            >
              <path d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' />
            </svg>
          </button>
          <span
            className='text-sm font-semibold text-blue-600 dark:text-blue-400
                           bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700
                           px-3 py-1 rounded-full'
          >
            {total} {t('TRACKING.RESULTS')}
          </span>
        </div>
      </div>

      {/* ─ Filters ─ */}
      <div
        className='bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700
                      px-6 py-3 flex items-center gap-3 flex-wrap'
      >
        {/* Search */}
        <div className='relative'>
          <svg
            className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            viewBox='0 0 24 24'
          >
            <circle cx='11' cy='11' r='8' />
            <path d='m21 21-4.35-4.35' />
          </svg>
          <input
            type='text'
            placeholder={t('TRACKING.SEARCH_PLACEHOLDER')}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className={`${inputBase} pl-9 w-64`}
          />
        </div>

        {/* Per page */}
        <select
          value={perPage}
          onChange={(e) => {
            setPerPage(Number(e.target.value));
            setPage(1);
          }}
          className={inputBase}
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n} / {t('TRACKING.PAGE')}
            </option>
          ))}
        </select>

        {/* Stats — from current page */}
        <div className='ml-auto flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400'>
          <span className='flex items-center gap-1.5'>
            <span className='w-2 h-2 rounded-full bg-green-500' />
            {pageViewCount} {t('TRACKING.PAGE_VIEWS')}
          </span>
          <span className='flex items-center gap-1.5'>
            <span className='w-2 h-2 rounded-full bg-blue-500' />
            {mobileCount} {t('TRACKING.MOBILE')}
          </span>
        </div>
      </div>

      {/* ─ Error Banner ─ */}
      {error && (
        <div
          className='mx-6 mt-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200
                        dark:border-red-700 rounded-xl text-sm text-red-600 dark:text-red-400 flex items-center gap-2'
        >
          <span>⚠️</span> {error}
          <button onClick={fetchData} className='ml-auto underline font-medium'>
            Retry
          </button>
        </div>
      )}

      {/* ─ Table ─ */}
      <div className='p-6'>
        <div
          className='bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700
                        shadow-sm overflow-hidden'
        >
          <div className='overflow-x-auto'>
            <table className='w-full min-w-[960px]'>
              <thead>
                <tr className='bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700'>
                  <th className='px-4 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-10'>
                    #
                  </th>
                  <Th col='sessionId'>{t('TRACKING.SESSION_ID')}</Th>
                  <Th col='city'>{t('TRACKING.LOCATION')}</Th>
                  <Th col='ipAddress'>{t('TRACKING.IP_ADDRESS')}</Th>
                  <Th col='referrer'>{t('TRACKING.REFERRER')}</Th>
                  <th className='px-4 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                    {t('TRACKING.EVENTS')}
                  </th>
                  <th className='px-4 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                    {t('TRACKING.DEVICE')}
                  </th>
                  <Th col='createdAt'>{t('TRACKING.CREATED_AT')}</Th>
                </tr>
              </thead>

              <tbody className='divide-y divide-gray-100 dark:divide-gray-700/50'>
                {/* Loading skeleton */}
                {loading &&
                  data.length === 0 &&
                  Array.from({ length: perPage > 10 ? 10 : perPage }).map(
                    (_, i) => <LogoLoading  />,
                  )}

                {/* Empty state */}
                {!loading && data.length === 0 && !error && (
                  <tr>
                    <td colSpan={8} className='text-center py-20'>
                      <div className='flex flex-col items-center gap-2'>
                        <span className='text-4xl'>📭</span>
                        <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                         {t('TRACKING.NO_EVENTS')}
                        </p>
                        <p className='text-xs text-gray-400 dark:text-gray-500'>
                          {debouncedSearch
                            ? "Try a different search term"
                            : "No guest tracking data yet"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Data rows */}
                {data.map((r, idx) => (
                  <tr
                    key={r._id}
                    onClick={() => setSelected(r)}
                    className={`hover:bg-slate-50 dark:hover:bg-gray-800/60 cursor-pointer transition-colors group
                      ${loading ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    {/* # */}
                    <td className='px-4 py-3.5 text-xs text-gray-400 dark:text-gray-500 font-mono'>
                      {(page - 1) * perPage + idx + 1}
                    </td>

                    {/* Session ID */}
                    <td className='px-4 py-3.5'>
                      <span className='font-mono text-xs text-blue-600 dark:text-blue-400 group-hover:underline'>
                        {r.sessionId ? r.sessionId.slice(0, 26) + "…" : "—"}
                      </span>
                    </td>

                    {/* Location */}
                    <td className='px-4 py-3.5'>
                      <p className='text-sm font-medium text-gray-800 dark:text-gray-200'>
                        {r.city ? `📍 ${r.city}` : "—"}
                      </p>
                      <p className='text-xs text-gray-400 dark:text-gray-500'>
                        {r.state || ""}
                      </p>
                    </td>

                    {/* IP */}
                    <td className='px-4 py-3.5'>
                      <span
                        className='font-mono text-xs text-gray-600 dark:text-gray-300
                                       bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-md'
                      >
                        {r.ipAddress || "—"}
                      </span>
                    </td>

                    {/* Referrer */}
                    <td className='px-4 py-3.5'>
                      <span className='text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1.5'>
                        {refIcon(r.referrer)}
                        <span className='truncate max-w-[130px]'>
                          {!r.referrer
                            ? "—"
                            : r.referrer === "direct"
                              ? "Direct"
                              : r.referrer
                                  .replace(/https?:\/\//, "")
                                  .replace(/\/.*/, "")}
                        </span>
                      </span>
                    </td>

                    {/* Events */}
                    <td className='px-4 py-3.5'>
                      <div className='flex items-center gap-1.5 flex-wrap'>
                        {Array.isArray(r.events) && r.events.length > 0 ? (
                          <>
                            {r.events.slice(0, 2).map((ev, i) => (
                              <EventBadge key={i} type={ev.type} />
                            ))}
                            {r.events.length > 2 && (
                              <span className='text-xs text-gray-400 dark:text-gray-500'>
                                +{r.events.length - 2}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className='text-xs text-gray-400 dark:text-gray-500'>
                            —
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Device */}
                    <td className='px-4 py-3.5'>
                      <span className='text-sm text-gray-600 dark:text-gray-300 capitalize'>
                        {(r.deviceType || r.device?.platform)
                          ? `${platformIcon(r.deviceType || r.device?.platform)} ${r.deviceType || r.device?.platform}`
                          : "—"}
                      </span>
                    </td>

                    {/* Date */}
                    <td className='px-4 py-3.5'>
                      <span className='font-mono text-xs text-gray-500 dark:text-gray-400'>
                        {fmtDate(r.createdAt)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ─ Pagination ─ */}
          <div
            className='border-t border-gray-100 dark:border-gray-700 px-5 py-3
                          flex items-center justify-between bg-gray-50 dark:bg-gray-800/50'
          >
            <span className='text-sm text-gray-500 dark:text-gray-400'>
              {t('PAGINATION.SHOWING')}{" "}
              <span className='font-semibold text-gray-700 dark:text-gray-300'>
                {total === 0 ? 0 : (page - 1) * perPage + 1}
              </span>
              {" – "}
              <span className='font-semibold text-gray-700 dark:text-gray-300'>
                {Math.min(page * perPage, total)}
              </span>
              {" of "}
              <span className='font-semibold text-gray-700 dark:text-gray-300'>
                {total}
              </span>
            </span>

            <div className='flex items-center gap-1'>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                className='px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                           bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300
                           hover:bg-gray-50 dark:hover:bg-gray-700
                           disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
              >
                ← {t('TRACKING.PREV')}
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let p;
                if (totalPages <= 5) p = i + 1;
                else if (page <= 3) p = i + 1;
                else if (page >= totalPages - 2) p = totalPages - 4 + i;
                else p = page - 2 + i;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    disabled={loading}
                    className={`w-8 h-8 text-sm rounded-lg border transition-colors
                      ${
                        p === page
                          ? "bg-blue-600 dark:bg-blue-500 text-white border-blue-600 font-bold"
                          : "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      } disabled:opacity-50`}
                  >
                    {p}
                  </button>
                );
              })}

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || loading}
                className='px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                           bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300
                           hover:bg-gray-50 dark:hover:bg-gray-700
                           disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
              >
                {t('COMMON.NEXT')} →
              </button>
            </div>
          </div>
        </div>

        <p className='text-center text-xs text-gray-400 dark:text-gray-500 mt-3'>
         {t('TRACKING.CLICK_TO_VIEW_DETAILS')}
        </p>
      </div>
      <DetailDrawer record={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
